import type { DateSelectArg, EventApi } from '@fullcalendar/core';
import type { Absence, ResourceUser } from '@src/features/absence';
import { formatIsoDate, isNextDay, subtractDay, toTime } from '@src/shared/lib';
import type { SelectedAbsence, TimelineSlot, VisibleRange } from '../types';
import {
  formatDurationLabel,
  getAbsencesIdsForRange,
  getEventLabel,
  getUserAbsences,
} from './absence';
import { ABSENCE_BLOCK_POSITION_SINGLE } from './constants';
import { getUserWorktimeForDate } from './resources';

const FIXED_MONTH_DAY_COUNT = 31;

export function getFixedMonthVisibleRange(anchorDate: Date): VisibleRange {
  const start = new Date(
    anchorDate.getFullYear(),
    anchorDate.getMonth(),
    anchorDate.getDate(),
  );
  const end = new Date(start);
  end.setDate(end.getDate() + FIXED_MONTH_DAY_COUNT);

  return { start, end };
}

/**
 * FullCalendar's ScrollGrid throttles layout passes to 10 per
 * SCROLLGRID_RESIZE_INTERVAL (500 ms) and silently drops the rest -- including
 * the forced ones `updateSize()` triggers. When a burst of re-renders outruns
 * that budget the last, correct render never gets measured: the resource area
 * keeps its natural row heights while the timeline lanes fall back to
 * `innerHeight: ''` and collapse, so only rows holding an event stay visible.
 * Re-measuring once the burst is over repairs it -- the same trick FullCalendar
 * itself uses in ResourceTimelineView.ensureScrollGridResize.
 */
export const SCROLLGRID_SETTLE_MS = 600;

/**
 * Whether the block behind a selection may run past the range in memory.
 *
 * The absences on screen are fetched for the visible window plus a day either
 * side, so a run carrying on beyond it always shows a day at or past the
 * boundary -- the day after that was never loaded. A selection built from
 * those rows therefore names only the part that happens to be on screen, which
 * matters well beyond looking wrong: approving it would approve those days and
 * quietly leave the rest of the absence undecided.
 */
export function isSelectionClipped(
  selection: SelectedAbsence,
  visibleRange: VisibleRange,
): boolean {
  return clippedSides(selection, visibleRange) !== null;
}

function clippedSides(
  selection: SelectedAbsence,
  visibleRange: VisibleRange,
): 'start' | 'end' | 'both' | null {
  if (!selection.start || !selection.end) {
    return null;
  }
  const start = new Date(`${selection.start}T00:00:00`);
  const end = new Date(`${selection.end}T00:00:00`);
  const before = start < visibleRange.start;
  const after = end >= visibleRange.end;

  if (before && after) return 'both';
  if (before) return 'start';
  if (after) return 'end';
  return null;
}

/**
 * Where to put the window so a clipped selection is shown whole.
 *
 * Normally a week before the absence, the same run-up a deep link opens with,
 * so it is read in the context of the days around it rather than starting
 * flush against the edge.
 *
 * The window is a fixed 31 days, so a long run cannot afford the week. Then it
 * takes the whole window, pinned to whichever end is still hidden: a run
 * overhanging the right edge gets pinned to its first day, one overhanging the
 * left gets its last known day put at the far end. Either way the days that
 * were missing come back in one move rather than one day at a time.
 *
 * Returns null when nothing needs to move, or when the run is longer than the
 * window can hold at all and moving would only trade one clipped end for the
 * other.
 */
export function getFocusWindowStart(
  selection: SelectedAbsence,
  visibleRange: VisibleRange,
): Date | null {
  const sides = clippedSides(selection, visibleRange);
  if (sides === null || sides === 'both') {
    return null;
  }

  const first = new Date(`${selection.start}T00:00:00`);
  const last = new Date(`${selection.end}T00:00:00`);
  if (daysCovered(first, last) > FIXED_MONTH_DAY_COUNT) {
    return null;
  }

  let anchor = addDays(first, -LEAD_IN_DAYS);
  if (daysCovered(anchor, last) > FIXED_MONTH_DAY_COUNT) {
    anchor =
      sides === 'start' ? addDays(last, -(FIXED_MONTH_DAY_COUNT - 1)) : first;
  }

  const start = new Date(
    anchor.getFullYear(),
    anchor.getMonth(),
    anchor.getDate(),
  );
  return start.getTime() === visibleRange.start.getTime() ? null : start;
}

/** How many days a window has to span to hold both ends inclusively. */
function daysCovered(first: Date, last: Date): number {
  const day = 24 * 60 * 60 * 1000;
  return Math.round((last.getTime() - first.getTime()) / day) + 1;
}

function addDays(value: Date, days: number): Date {
  const shifted = new Date(value);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
}

/**
 * Days of run-up left before an absence the calendar is being opened on.
 *
 * A week, so the absence is read in the context of the days around it rather
 * than starting flush against the edge. The remaining three weeks of the
 * window are room for it to run into; anything longer gives up the run-up
 * rather than the absence.
 */
const LEAD_IN_DAYS = 7;

/**
 * The 31-day window the calendar opens on.
 *
 * `anchorIsoDate` lets a deep link (a task reference naming an absence in
 * another month) seed the range up front, so the calendar mounts where it is
 * wanted instead of jumping there imperatively a frame later.
 *
 * With an anchor the window opens just before it rather than on the first of
 * its month, which is what keeps a deep link to a single range change. A month
 * is a poor window for something that does not respect months: an absence
 * starting on the 28th has three days of it left to run into and spills out,
 * and moving the window afterwards to take in the rest costs a second grid
 * rebuild -- the one that outruns ScrollGrid's layout budget and collapses the
 * lanes for a moment. Opening a few days early leaves nearly the whole window
 * ahead of it, so there is usually nothing to correct.
 */
export function getInitialVisibleRange(
  anchorIsoDate?: string | null,
): VisibleRange {
  const anchor = anchorIsoDate
    ? new Date(`${anchorIsoDate}T00:00:00`)
    : new Date();

  if (anchorIsoDate && !Number.isNaN(anchor.getTime())) {
    return getFixedMonthVisibleRange(addDays(anchor, -LEAD_IN_DAYS));
  }

  const fallback = Number.isNaN(anchor.getTime()) ? new Date() : anchor;
  return getFixedMonthVisibleRange(
    new Date(fallback.getFullYear(), fallback.getMonth(), 1),
  );
}

/**
 * Collects absence details for a given absence ID.
 *
 * Retrieves key attributes from the targeted absence record and, if available,
 * from its associated neighbors (whole activity).
 *
 * @param {number} absenceId
 *        Unique identifier of the absence to collect.
 * @param {Array<Absence>} absences
 *        List of absence objects to search through.
 * @param {boolean} single
 *        Gets data for single absenece object with specified id or
 *        for all absences if it is multiday absence event
 */
export function getAbsenceSelectionById(
  absenceId: number,
  absences: Absence[],
  single = false,
): SelectedAbsence | null {
  if (!absenceId || !Array.isArray(absences) || absences.length === 0) {
    return null;
  }

  const anchor = absences.find((a) => a.id === absenceId);
  if (!anchor) {
    return null;
  }

  if (
    single ||
    !anchor.allDay ||
    anchor.blockPosition === ABSENCE_BLOCK_POSITION_SINGLE
  ) {
    return hydrateSelectedAbsence(
      {
        userId: anchor.userId,
        start: formatIsoDate(anchor.date),
        end: formatIsoDate(anchor.date),
        startTime: anchor.startTime,
        endTime: anchor.endTime,
        absenceIds: [anchor.id],
      },
      absences,
    );
  }

  const block = getAbsenceBlock(anchor, absences);
  if (!block.length) {
    return null;
  }

  return hydrateSelectedAbsence(
    {
      userId: block[0].userId,
      start: formatIsoDate(block[0].date),
      end: formatIsoDate(block[block.length - 1].date),
      startTime: null,
      endTime: null,
      absenceIds: block.map((a) => a.id),
    },
    absences,
  );
}

/**
 * The run of days a withdrawal request covers, around `anchor`.
 *
 * The server flags every day it named, not just the one the task points at, so
 * the run is read off the flags rather than guessed at from the block. It can
 * be a slice out of the middle of a longer entry -- days being given back
 * because a change is replacing them, say -- which is why the walk stops at the
 * first unflagged day either side.
 */
function getWithdrawalRun(anchor: Absence, absences: Absence[]): Absence[] {
  const block = getAbsenceBlock(anchor, absences);
  const anchorIdx = block.findIndex((a) => a.id === anchor.id);
  if (anchorIdx === -1) {
    return [];
  }

  let left = anchorIdx;
  while (left - 1 >= 0 && block[left - 1].withdrawalRequested) {
    left -= 1;
  }
  let right = anchorIdx;
  while (right + 1 < block.length && block[right + 1].withdrawalRequested) {
    right += 1;
  }
  return block.slice(left, right + 1);
}

/**
 * Selection for the absence a task reference points at.
 *
 * Every raiser hangs its task off the first day of what it is about. A
 * withdrawal request is the one that need not be about a whole entry: it can
 * name a slice out of the middle, either because the user asked for those days
 * back or because a change is replacing them, so the days it named are what
 * gets selected. Everything else selects the block, or the single day when the
 * task points into the middle of one.
 */
export function getAbsenceReferenceSelection(
  absenceId: number,
  absences: Absence[],
): SelectedAbsence | null {
  const anchor = absences.find((a) => a.id === absenceId);
  if (!anchor) {
    return null;
  }

  if (anchor.allDay && anchor.withdrawalRequested) {
    const run = getWithdrawalRun(anchor, absences);
    if (run.length) {
      return hydrateSelectedAbsence(
        {
          userId: run[0].userId,
          start: formatIsoDate(run[0].date),
          end: formatIsoDate(run[run.length - 1].date),
          startTime: null,
          endTime: null,
          absenceIds: run.map((a) => a.id),
        },
        absences,
      );
    }
  }

  const block = anchor.allDay ? getAbsenceBlock(anchor, absences) : [];
  const isBlockHead = block.length === 0 || block[0].id === anchor.id;

  return getAbsenceSelectionById(absenceId, absences, !isBlockHead);
}

export function getAbsenceBlock(
  anchor: Absence,
  absences: Absence[],
): Absence[] {
  const sameGroup = (a: Absence) => {
    return (
      anchor.typeId != null &&
      a.userId === anchor.userId &&
      a.allDay &&
      a.typeId === anchor.typeId &&
      a.absenceTentative === anchor.absenceTentative &&
      a.approvedManager === anchor.approvedManager &&
      a.approvedHr === anchor.approvedHr
    );
  };

  const candidates = absences
    .filter(sameGroup)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const anchorIdx = candidates.findIndex((a) => a.id === anchor.id);
  if (anchorIdx === -1) {
    return [];
  }

  let left = anchorIdx;
  while (
    left - 1 >= 0 &&
    isNextDay(candidates[left - 1].date, candidates[left].date)
  ) {
    left -= 1;
  }

  let right = anchorIdx;
  while (
    right + 1 < candidates.length &&
    isNextDay(candidates[right].date, candidates[right + 1].date)
  ) {
    right += 1;
  }

  return candidates.slice(left, right + 1);
}

export function hydrateSelectedAbsence(
  selection: SelectedAbsence,
  absences: Absence[],
): SelectedAbsence {
  if (!selection.absenceIds.length || !absences.length) {
    return selection;
  }

  const snapshotAbsences = selection.absenceIds
    .map((id) => absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => !!absence);

  if (!snapshotAbsences.length) {
    return selection;
  }

  const primaryAbsenceId =
    selection.primaryAbsenceId ?? selection.absenceIds[0];
  const primaryAbsence =
    snapshotAbsences.find((absence) => absence.id === primaryAbsenceId) ??
    snapshotAbsences[0];
  const blockHead =
    primaryAbsence.allDay &&
    primaryAbsence.blockPosition !== ABSENCE_BLOCK_POSITION_SINGLE
      ? getAbsenceBlock(primaryAbsence, absences)[0] ?? primaryAbsence
      : primaryAbsence;

  return {
    ...selection,
    primaryAbsenceId: primaryAbsence.id,
    typeId: primaryAbsence.typeId ?? null,
    absenceTentative: primaryAbsence.absenceTentative ?? false,
    approvedManager: primaryAbsence.approvedManager ?? null,
    approvedHr: primaryAbsence.approvedHr ?? null,
    notes: blockHead.notes ?? primaryAbsence.notes ?? '',
    snapshotAbsences,
  };
}

export function getSlotDatefromSelection(
  info: DateSelectArg,
  absences: Absence[],
): TimelineSlot {
  const end = info.allDay ? subtractDay(info.end, 1) : info.end;
  const userId = info.resource?.extendedProps.userId;
  const userAbsences = getUserAbsences(userId, absences);
  const startTime = info.allDay ? null : toTime(info.start);
  const endTime = info.allDay ? null : toTime(info.end);
  const slot: TimelineSlot = {
    resourceId: info?.resource?.id,
    start: formatIsoDate(info.start),
    end: formatIsoDate(end),
    startTime,
    endTime,
    selectedIds: getAbsencesIdsForRange(info.start, end, userAbsences, {
      dayView: !info.allDay,
      startTime,
      endTime,
    }),
    editedAbsenceId: null,
    userId: info.resource?.extendedProps.userId,
  };
  return slot;
}

export function getSlotFromEvent(event: EventApi): TimelineSlot | null {
  if (!event.start) return null;
  if (!event.end) return null;

  const absenceId = Number(event.extendedProps.absenceId ?? event.id);
  const end = event.allDay ? subtractDay(event.start, 1) : event.end;
  return {
    resourceId: event.getResources()[0].id,
    start: formatIsoDate(event.startStr),
    end: formatIsoDate(end),
    startTime: event.allDay ? null : toTime(event.start),
    endTime: event.allDay ? null : toTime(event.end),
    selectedIds: [absenceId],
    editedAbsenceId: absenceId,
    userId: event.getResources()[0].extendedProps.userId,
  };
}

/*
   Returns an array of free dates  within the slot range that are
   not occupied by absences.
*/
export function getSlotFreeDates(
  slot: TimelineSlot,
  absences: Absence[],
): Date[] {
  if (!slot || !slot.start || !slot.end) {
    return [];
  }

  if (slot.end < slot.start) {
    return [];
  }
  const start = slot.start;
  const end = slot.end;

  const occupied = new Set(
    (absences || [])
      .filter((a) => !(a.dateStr < start) && !(a.dateStr > end))
      .map((a) => a.dateStr),
  );
  const free = [];
  let cur = new Date(slot.start);

  while (cur <= new Date(slot.end)) {
    if (!occupied.has(formatIsoDate(cur))) {
      free.push(cur);
    }
    const nextDate = cur.getDate() + 1;
    cur = new Date(cur);
    cur.setDate(nextDate);
  }
  return free;
}

export function getTransientEventsForSlot(
  slot: TimelineSlot,
  absences: Array<Absence>,
  users: Array<ResourceUser>,
  dayView = false,
) {
  if (!slot || !slot.start || !slot.end || !slot.userId) {
    return [];
  }
  const userAbsences = getUserAbsences(slot.userId, absences);
  if (dayView) {
    if (slot.editedAbsenceId) {
      return [];
    }
    const overlappingIds = getAbsencesIdsForRange(
      slot.start,
      slot.end,
      userAbsences,
      {
        dayView: true,
        startTime: slot.startTime,
        endTime: slot.endTime,
        excludeIds: slot.editedAbsenceId ? [slot.editedAbsenceId] : [],
      },
    );
    if (overlappingIds.length > 0) {
      return [];
    }

    const userWorktime = getUserWorktimeForDate(slot.userId, users, slot.start);
    const startTime = slot.startTime || userWorktime?.start;
    const endTime = slot.endTime || userWorktime?.end;
    const label =
      startTime && endTime
        ? formatDurationLabel(startTime, endTime)
        : getEventLabel(slot.start, slot.end, true);
    return [
      {
        id: `transient-event-${slot.start}`,
        resourceId: slot.resourceId,
        start: formatIsoDate(slot.start, startTime),
        end: formatIsoDate(slot.end, endTime),
        classNames: [
          'transient-event',
          'transient-edge-left',
          'transient-edge-right',
        ],
        extendedProps: { label: label },
        startEditable: false,
        durationEditable: false,
        resourceEditable: false,
      },
    ];
  }

  const slotFreeDates = getSlotFreeDates(slot, userAbsences);
  if (!slotFreeDates.length) {
    return [];
  }
  // const user = users.find((u) => u.id === slot.userId);
  // const worktime = user.worktime;

  const transientEvents = slotFreeDates.map((day, idx) => {
    const label = getEventLabel(day, day, dayView);
    const isLast = idx === slotFreeDates.length - 1;
    const event = {
      id: `transient-event-${formatIsoDate(day)}`,
      resourceId: slot.resourceId,
      start: formatIsoDate(day),
      end: formatIsoDate(day),
      classNames: [
        'transient-event',
        'transient-month-event',
        'transient-edge-left',
        isLast ? 'transient-edge-right' : 'transient-join-right',
      ],
      extendedProps: { label: label },
      editable: false,
    };

    return event;
  });
  return transientEvents;
}

/**
 * Absences the calendar should paint as the active selection.
 *
 * Editing reshapes one row, so the edited absence stays lit wherever it moves.
 * Withdrawing is the opposite: the edited ids only bound how far the withdrawal
 * may reach, so the live range in `selectedIds` is what should light up -- the
 * day that happened to be clicked must not stay selected once the range moves
 * off it.
 */
export function getActiveAbsenceIds(slot: TimelineSlot | null): number[] {
  if (!slot) return [];
  if (slot.withdrawing) return slot.selectedIds;
  return slot.editedAbsenceId ? [slot.editedAbsenceId] : slot.selectedIds;
}

export function isAbsenceSelected(
  slot: TimelineSlot,
  absences: Absence[],
): slot is TimelineSlot & { start: string; end: string; userId: number } {
  if (!slot.start || !slot.end || !slot.userId || !slot.selectedIds.length) {
    return false;
  }

  const userAbsences = getUserAbsences(slot.userId, absences);
  if (getSlotFreeDates(slot, userAbsences).length > 0) {
    return false;
  }

  const selectedIds = [...slot.selectedIds].sort((a, b) => a - b);

  const selectedAbsences = selectedIds
    .map((id) => absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => !!absence);

  if (selectedAbsences.length !== selectedIds.length) {
    return false;
  }

  const [firstAbsence] = selectedAbsences;
  if (!firstAbsence) {
    return false;
  }

  const groupedSelection = getAbsenceSelectionById(firstAbsence.id, absences);
  const singleSelection = getAbsenceSelectionById(
    firstAbsence.id,
    absences,
    true,
  );

  const isWithinGroupedSelection = groupedSelection
    ? slot.userId === groupedSelection.userId &&
      slot.start >= groupedSelection.start &&
      slot.end <= groupedSelection.end
    : false;

  const allSameType = selectedAbsences.every(
    (absence) =>
      firstAbsence.typeId != null &&
      absence.userId === firstAbsence.userId &&
      absence.typeId === firstAbsence.typeId &&
      absence.absenceTentative === firstAbsence.absenceTentative &&
      absence.approvedManager === firstAbsence.approvedManager &&
      absence.approvedHr === firstAbsence.approvedHr &&
      absence.allDay === firstAbsence.allDay,
  );

  const matchesTimes = selectedAbsences.every(
    (absence) =>
      absence.startTime === firstAbsence.startTime &&
      absence.endTime === firstAbsence.endTime,
  );

  const matchesSingleSelection =
    !!singleSelection &&
    slot.start === singleSelection.start &&
    slot.end === singleSelection.end &&
    slot.startTime === singleSelection.startTime &&
    slot.endTime === singleSelection.endTime;

  return (
    isWithinGroupedSelection &&
    allSameType &&
    (matchesTimes || matchesSingleSelection)
  );
}

export function isFullyOccupiedSlot(
  slot: TimelineSlot,
  absences: Absence[],
): slot is TimelineSlot & { start: string; end: string; userId: number } {
  if (!slot.start || !slot.end || !slot.userId || !slot.selectedIds.length) {
    return false;
  }

  const userAbsences = getUserAbsences(slot.userId, absences);
  if (getSlotFreeDates(slot, userAbsences).length > 0) {
    return false;
  }

  const isSingleDay = slot.start === slot.end;
  if (isSingleDay) {
    return true;
  }

  const selectedAbsences = slot.selectedIds
    .map((id) => absences.find((a) => a.id === id))
    .filter((a): a is Absence => !!a);
  const first = selectedAbsences[0];

  return (
    !!first &&
    selectedAbsences.every(
      (a) =>
        a.typeId === first.typeId &&
        a.userId === first.userId &&
        a.absenceTentative === first.absenceTentative &&
        a.approvedManager === first.approvedManager &&
        a.approvedHr === first.approvedHr &&
        a.allDay === first.allDay,
    )
  );
}
